// src/pages/settings/RolesTab.tsx — onglet Rôles (assignation d'un rôle à chaque utilisateur)

import { useState, useCallback, useEffect } from "react";
import { BtnEdit, BtnDelete } from "../../components/IconButtons";
import {
  type UserRole, ASSIGNABLE_ROLES, ROLE_LABELS,
} from "../../auth/roles";
import {
  listRoles, setRole as apiSetRole, removeRole as apiRemoveRole,
  type UserRoleEntry,
} from "../../api/rolesApi";

export function RolesTab({ showToast }:{ showToast: (msg: string) => void }) {
  const [roleEntries,  setRoleEntries]  = useState<UserRoleEntry[]>([]);
  const [rolesLoading, setRolesLoading] = useState(false);
  const [rolesError,   setRolesError]   = useState<string|null>(null);
  const [editRoleUser, setEditRoleUser] = useState<UserRoleEntry|null>(null);
  const [newRoleValue, setNewRoleValue] = useState<UserRole>("lecteur");
  const [query,        setQuery]        = useState("");

  const loadRoles = useCallback(async () => {
    setRolesLoading(true); setRolesError(null);
    try { setRoleEntries(await listRoles()); }
    catch (e: unknown) { setRolesError((e as Error).message); }
    finally { setRolesLoading(false); }
  }, []);

  useEffect(() => { loadRoles(); }, [loadRoles]);

  const qr = query.trim().toLowerCase();
  const filteredRoleEntries = qr
    ? roleEntries.filter(e => `${e.name} ${e.email}`.toLowerCase().includes(qr))
    : roleEntries;

  return (
    <div style={{height:"100%",display:"flex",flexDirection:"column"}}>
      <div style={{flexShrink:0,paddingBottom:12}}>
        <div className="flex-between mb-12">
          <p style={{fontSize:13,color:"var(--text-muted)"}}>
            Assignez un rôle à chaque utilisateur qui s'est déjà connecté.
            Les utilisateurs sans rôle explicite ont le rôle <strong>Lecteur</strong> par défaut.
          </p>
          <button onClick={loadRoles} className="btn btn-ghost btn-sm">↻ Rafraîchir</button>
        </div>
        <input
          type="text" placeholder="Rechercher un utilisateur…"
          value={query} onChange={e=>setQuery(e.target.value)}
          className="field" style={{width:"100%",fontSize:13}}
        />
      </div>
      <div style={{flex:1,minHeight:0,overflowY:"auto",paddingBottom:24}}>
        {rolesLoading && <div style={{textAlign:"center",padding:40,color:"var(--text-muted)"}}>Chargement…</div>}
        {rolesError && (
          <div style={{background:"#fdecea",border:"1px solid #f5c6cb",borderRadius:8,padding:"12px 16px",marginBottom:20,fontSize:13,color:"var(--red)"}}>
            ⚠ {rolesError}
          </div>
        )}
        {!rolesLoading && !rolesError && (
          <div className="flex-col gap-10">
            {roleEntries.length === 0 && (
              <div className="card p-32" style={{textAlign:"center"}}>
                <div style={{fontSize:32,marginBottom:10}}>🔑</div>
                <p style={{fontSize:13,color:"var(--text-muted)"}}>Aucun rôle configuré. Les utilisateurs apparaissent ici après leur première connexion.</p>
              </div>
            )}
            {filteredRoleEntries.length===0 && roleEntries.length>0 && (
              <div className="card p-32" style={{textAlign:"center",color:"var(--text-faint)"}}>
                <div style={{fontSize:28,marginBottom:10}}>🔎</div>
                <p style={{fontSize:13}}>Aucun résultat pour « {query} ».</p>
              </div>
            )}
            {filteredRoleEntries.map(entry => (
              <div key={entry.email} className="card flex gap-16" style={{alignItems:"center",padding:"14px 18px"}}>
                <div style={{width:40,height:40,borderRadius:"50%",background:"#1a1a2e",display:"flex",alignItems:"center",justifyContent:"center",fontSize:18,flexShrink:0,color:"#00FF00",fontFamily:"var(--ff-title)",fontWeight:900}}>
                  {(entry.name||entry.email||"?")[0].toUpperCase()}
                </div>
                <div className="flex-1">
                  <div className="t-title" style={{fontSize:14,color:"var(--text-primary)"}}>{entry.name||entry.email}</div>
                  <div style={{fontSize:11,color:"var(--text-muted)"}}>{entry.email}</div>
                </div>
                {editRoleUser?.email === entry.email ? (
                  <div className="flex gap-8" style={{alignItems:"center"}}>
                    <select value={newRoleValue} onChange={e=>setNewRoleValue(e.target.value as UserRole)}
                      className="field" style={{fontSize:12,padding:"4px 8px",minWidth:140}}>
                      {ASSIGNABLE_ROLES.map(r=>(
                        <option key={r} value={r}>{ROLE_LABELS[r]}</option>
                      ))}
                    </select>
                    <button className="btn btn-black btn-sm" onClick={async()=>{
                      try {
                        await apiSetRole(entry.email, entry.name, newRoleValue);
                        setEditRoleUser(null);
                        showToast("Rôle mis à jour ✓");
                        loadRoles();
                      } catch(e:unknown){ showToast("Erreur : " + (e as Error).message); }
                    }}>Valider</button>
                    <button className="btn btn-ghost btn-sm" onClick={()=>setEditRoleUser(null)}>Annuler</button>
                  </div>
                ) : (
                  <div className="flex gap-8" style={{alignItems:"center"}}>
                    <span style={{fontSize:12,padding:"3px 10px",borderRadius:20,background:"#1a1a2e",color:"#00FF00",fontFamily:"var(--ff-title)",fontWeight:700,letterSpacing:0.5}}>
                      {ROLE_LABELS[entry.role as UserRole] ?? entry.role}
                    </span>
                    <BtnEdit onClick={()=>{setEditRoleUser(entry);setNewRoleValue(entry.role as UserRole);}} title="Modifier le rôle"/>
                    <BtnDelete onClick={async()=>{
                      try {
                        await apiRemoveRole(entry.email);
                        showToast("Rôle supprimé (revient à Lecteur) ✓");
                        loadRoles();
                      } catch(e:unknown){ showToast("Erreur : " + (e as Error).message); }
                    }} title="Supprimer le rôle (revient à Lecteur)"/>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
